import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getClasses,
    createClass,
    updateClass,
    deleteClass,
    type ClassData,
} from '../lib/api';
import { queryKeys } from '../lib/queryClient';

export function useClasses(token: string | null, schoolProfileId?: string) {
    return useQuery({
        queryKey: queryKeys.classes.list(schoolProfileId),
        queryFn: () => getClasses(token!, schoolProfileId),
        enabled: !!token && !!schoolProfileId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateClass(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Omit<ClassData, 'id'>) => createClass(data, token!),

        onMutate: async (newClass) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.classes.list(schoolProfileId) });
            const previousClasses = queryClient.getQueryData<ClassData[]>(queryKeys.classes.list(schoolProfileId));

            const tempId = `temp-${Date.now()}`;
            queryClient.setQueryData<ClassData[]>(
                queryKeys.classes.list(schoolProfileId),
                (old = []) => [...old, { ...newClass, id: tempId } as ClassData]
            );

            return { previousClasses, tempId };
        },

        onSuccess: (createdClass, _variables, context) => {
            queryClient.setQueryData<ClassData[]>(
                queryKeys.classes.list(schoolProfileId),
                (old = []) => old.map(c => c.id === context?.tempId ? createdClass : c)
            );
        },

        onError: (_err, _variables, context) => {
            if (context?.previousClasses) {
                queryClient.setQueryData(queryKeys.classes.list(schoolProfileId), context.previousClasses);
            }
        },
    });
}

export function useUpdateClass(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<ClassData> }) =>
            updateClass(id, data, token!),

        onMutate: async ({ id, data }) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.classes.list(schoolProfileId) });
            const previousClasses = queryClient.getQueryData<ClassData[]>(queryKeys.classes.list(schoolProfileId));

            queryClient.setQueryData<ClassData[]>(
                queryKeys.classes.list(schoolProfileId),
                (old = []) => old.map(c => c.id === id ? { ...c, ...data } : c)
            );

            return { previousClasses };
        },

        onSuccess: (updatedClass, { id }) => {
            queryClient.setQueryData<ClassData[]>(
                queryKeys.classes.list(schoolProfileId),
                (old = []) => old.map(c => c.id === id ? updatedClass : c)
            );
        },

        onError: (_err, _variables, context) => {
            if (context?.previousClasses) {
                queryClient.setQueryData(queryKeys.classes.list(schoolProfileId), context.previousClasses);
            }
        },
    });
}

export function useDeleteClass(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteClass(id, token!),

        onMutate: async (deletedId) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.classes.list(schoolProfileId) });
            const previousClasses = queryClient.getQueryData<ClassData[]>(queryKeys.classes.list(schoolProfileId));

            queryClient.setQueryData<ClassData[]>(
                queryKeys.classes.list(schoolProfileId),
                (old = []) => old.filter(c => c.id !== deletedId)
            );

            return { previousClasses };
        },

        onError: (_err, _deletedId, context) => {
            if (context?.previousClasses) {
                queryClient.setQueryData(queryKeys.classes.list(schoolProfileId), context.previousClasses);
            }
        },
    });
}
